import type { Channel } from '@/api/endpoints/channels'
import { instagramDestination, useChannelDestinations } from '@/api/endpoints/destinations'
import { Section } from '@/components/ds/Section'
import { VideoBrandSuggestion } from './destinations/VideoBrandSuggestion'
import { InstagramConnection } from './instagram/auth/InstagramConnection'
import { TiktokOAuthSection } from './tiktok/auth/TiktokOAuthSection'
import { YoutubeAccountCard } from './youtube/auth/YoutubeAccountCard'
import styles from './PlatformCard.module.scss'

interface PlatformsSectionProps {
  channel: Channel
}

/**
 * Seção "Plataformas" da tela do canal: um cartão por plataforma. O YouTube é
 * a principal (decide o que sai e quando); o Instagram é um destino, que
 * publica os mesmos vídeos. Substitui a antiga seção única de OAuth.
 *
 * Com um backend que ainda não tem os destinos (o front sai no merge, o skab no
 * pull), a rota responde 404 e sobra só o cartão do YouTube — sem erro.
 *
 * Canal TikTok segue como antes: os destinos existem só para canal do YouTube.
 */
export function PlatformsSection({ channel }: PlatformsSectionProps) {
  const isYoutube = channel.platform !== 'tiktok'
  const destinations = useChannelDestinations(channel.slug, isYoutube)

  if (!isYoutube) return <TiktokOAuthSection channel={channel} />

  const instagram = instagramDestination(destinations.data)

  return (
    <Section
      title="Plataformas"
      description={
        instagram
          ? 'O YouTube é a plataforma principal: decide o que sai e quando. Os destinos publicam os mesmos vídeos.'
          : undefined
      }
    >
      <div className={styles.cards}>
        <YoutubeAccountCard channel={channel} />
        {instagram ? <InstagramConnection channel={channel} destination={instagram} /> : null}
        {destinations.isError ? (
          <p className={styles.note} role="alert">
            Não foi possível carregar os destinos deste canal agora.
          </p>
        ) : null}
        <VideoBrandSuggestion slug={channel.slug} channelTag={channel.channel_tag} destinations={destinations.data} />
      </div>
    </Section>
  )
}
